import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAppSelector } from '@/redux/hooks'
import { RootState } from '@/redux/store'
import { Clock, MessageSquare } from 'lucide-react'
import { formatDate } from '@/utils/dateTimeUtils'
import { ExistingAIPlayerTestChat } from '@/redux/slices/aiPlayerTestChatSlice'

interface PreviousAIPlayerTestChatsModalProps {
  isOpen: boolean
  onClose: () => void
  onViewChat: (chatId: number) => void
}

const PreviousAIPlayerTestChatsModal: React.FC<
  PreviousAIPlayerTestChatsModalProps
> = ({ isOpen, onClose, onViewChat }) => {
  const { existingTestChats } = useAppSelector(
    (state: RootState) => state.aiPlayerTestChatMessages
  )

  const handleSelect = (chatId: number) => {
    onViewChat(chatId)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[700px] max-h-[80vh]'>
        <DialogHeader>
          <DialogTitle>Previous Test Chats</DialogTitle>
          <DialogDescription>
            Select a test chat to preview. Previous chats are read-only.
          </DialogDescription>
        </DialogHeader>
        <div className='overflow-y-auto max-h-[60vh]'>
          {existingTestChats.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <MessageSquare className='w-16 h-16 text-gray-400 mb-4' />
              <p className='text-gray-500 text-lg font-medium'>
                No previous test chats found
              </p>
              <p className='text-gray-400 text-sm mt-2'>
                Start a test chat to create one
              </p>
            </div>
          ) : (
            <div className='space-y-2'>
              {existingTestChats.map(
                (chat: ExistingAIPlayerTestChat, index: number) => (
                  <div
                    key={chat.id}
                    className='flex items-start gap-3 p-4 border rounded-lg transition-colors cursor-pointer hover:bg-gray-50'
                    onClick={() => handleSelect(chat.id)}
                  >
                    <div className='flex-1 min-w-0'>
                      <p className='font-medium text-gray-900'>{`Test Chat #${index + 1}`}</p>
                      <div className='flex items-center gap-4 mt-2 text-sm text-gray-600'>
                        <div className='flex items-center gap-1'>
                          <MessageSquare className='w-4 h-4' />
                          <span>{chat.message_count} messages</span>
                        </div>
                        <div className='flex items-center gap-1'>
                          <Clock className='w-4 h-4' />
                          <span>{formatDate(chat.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
        <div className='flex justify-end pt-2'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PreviousAIPlayerTestChatsModal
